import React from "react";
import { Check, CalendarClock, Bell, Car, CreditCard, Radio } from "lucide-react";
import RoutePoints from "@/components/price/RoutePoints";

const CONFETTI = ["#FF3B3B", "#FFB800", "#3BB273", "#3B82F6", "#FF3B3B", "#FFB800", "#3BB273", "#3B82F6"];

// Écran de confirmation après création d'une course programmée.
export default function ScheduledConfirmScreen({ ride, onOpenList, onHome }) {
  const d = new Date(ride.scheduled_at);
  const weekday = d.toLocaleDateString("fr-FR", { weekday: "long" });
  const month = d.toLocaleDateString("fr-FR", { month: "long" });
  const time = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  const accepted = ["scheduled_accepted", "scheduled_awaiting_payment", "accepted"].includes(ride.status);
  const card = ride.payment_method === "card";
  const steps = [
    { icon: Radio, label: "On prévient les covoitureurs de ta zone", done: true, active: !accepted },
    {
      icon: card ? CreditCard : Car,
      label: accepted ? `${ride.driver_name || "Un covoitureur"} a accepté !` : "Un covoitureur accepte ta course",
      sub: card ? "Tu paies seulement à ce moment-là" : "Tu paies en espèces le jour J",
      done: accepted, active: accepted,
    },
    { icon: Bell, label: "Rappel 1 h avant, puis suivi en direct", sub: "Sans covoitureur 15 min avant, la demande expire", done: false },
  ];
  return (
    <div className="fixed inset-0 z-[100] flex justify-center overflow-y-auto text-foreground" style={{ background: "hsl(15 100% 96%)" }} data-testid="client-scheduled-confirm">
      <div className="relative flex w-full max-w-md flex-col street-dots px-5" style={{ paddingTop: "max(20px, env(safe-area-inset-top))", paddingBottom: "max(24px, env(safe-area-inset-bottom))" }}>
        <div className="pointer-events-none absolute inset-x-0 top-0 h-48 overflow-hidden" aria-hidden="true">
          {CONFETTI.map((c, i) => (
            <span key={i} className="sched-confetti absolute top-0 h-3 w-2 rounded-sm" style={{ left: `${8 + i * 11}%`, background: c, animationDelay: `${i * 0.28}s` }} />
          ))}
        </div>

        <div className="inline-flex w-fit items-center gap-1.5 rounded-full bg-accent px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-accent-foreground street-border-thin street-shadow-sm">
          <CalendarClock className="h-3.5 w-3.5" /> Course programmée
        </div>
        <h1 className="mt-3 font-[Outfit] text-[26px] font-black leading-[1.02] tracking-tight animate-fade-up">
          C&apos;est noté,<br />
          <span className="inline-block mt-1 rounded-xl bg-primary px-2 py-0.5 text-primary-foreground street-border-thin rotate-[-2deg]">on s&apos;en occupe !</span>
        </h1>

        <div className="relative mx-auto mt-4 w-48">
          <div className="sched-flip overflow-hidden rounded-3xl bg-white text-center street-border street-shadow" data-testid="scheduled-confirm-date">
            <div className="bg-primary py-2 text-[12px] font-black uppercase tracking-widest text-primary-foreground border-b-2 border-black">{weekday}</div>
            <div className="py-2">
              <div className="font-[Outfit] text-[52px] font-black leading-none tabular-nums">{d.getDate()}</div>
              <div className="mt-1 text-[13px] font-black uppercase tracking-widest text-foreground/60">{month}</div>
              <div className="mx-auto mt-3 w-fit rounded-full bg-accent px-3 py-1 font-[Outfit] text-[20px] font-black tabular-nums street-border-thin">{time}</div>
            </div>
          </div>
          <div className="sched-stamp absolute -right-5 -top-4 grid h-14 w-14 place-items-center rounded-full bg-[#3BB273] street-border street-shadow-sm">
            <Check className="h-7 w-7 text-white" strokeWidth={4} />
          </div>
        </div>

        <div className="mt-4 rounded-3xl bg-white p-3 street-border street-shadow animate-fade-up" style={{ animationDelay: "250ms" }}>
          <div className="flex items-start gap-3">
            <RoutePoints from={ride.from_address} to={ride.to_address} size="sm" truncate className="flex-1" />
            <div className="text-right">
              <div className="font-[Outfit] text-[20px] font-black tabular-nums text-primary">{Number(ride.price_eur).toFixed(2)}€</div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-foreground/50">{card ? "Carte" : "Espèces"}</div>
            </div>
          </div>
        </div>

        <ol className="mt-3 space-y-1.5" data-testid="scheduled-confirm-steps">
          {steps.map((s, i) => <Step key={i} s={s} i={i} />)}
        </ol>

        <div className="mt-auto pt-4 space-y-2">
          <button type="button" onClick={onOpenList} data-testid="scheduled-confirm-list" className="street-press flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-3.5 font-[Outfit] text-[15px] font-black text-primary-foreground street-border street-shadow">
            <CalendarClock className="h-5 w-5" /> Voir mes courses programmées
          </button>
          <button type="button" onClick={onHome} data-testid="scheduled-confirm-home" className="street-press w-full rounded-2xl bg-white py-3 font-[Outfit] text-[14px] font-black street-border-thin">
            Retour à l&apos;accueil
          </button>
        </div>
      </div>
    </div>
  );
}

function Step({ s, i }) {
  const Icon = s.icon;
  return (
    <li className="flex items-start gap-3 rounded-2xl bg-white/80 p-2.5 street-border-thin animate-fade-up" style={{ animationDelay: `${400 + i * 120}ms` }}>
      <div className={`relative grid h-9 w-9 flex-none place-items-center rounded-xl street-border-thin ${s.done ? "bg-[#D6F5D6]" : "bg-white"}`}>
        {s.active && <span className="absolute inset-0 rounded-xl bg-primary/30 animate-ping" />}
        {s.done && !s.active ? <Check className="h-4 w-4" strokeWidth={3} /> : <Icon className="h-4 w-4" strokeWidth={2.5} />}
      </div>
      <div className="min-w-0">
        <div className="font-[Outfit] text-[13px] font-black leading-tight">{s.label}</div>
        {s.sub && <div className="mt-0.5 text-[11px] font-medium text-foreground/60">{s.sub}</div>}
      </div>
    </li>
  );
}
